import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Plus,
  Search,
  Layers,
  Copy,
  Pencil,
  Trash2,
  ExternalLink,
  Clock,
  Briefcase,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import api from '../../../../api/client';
import ProfileFormModal from './ProfileFormModal';
import ProfileQuestionStudio from './ProfileQuestionStudio';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import NotificationPopup from '../../component/NotificationPopup';
import {
  EXPERIENCE_LEVEL_OPTIONS,
  getExperienceLevelLabel,
  getExperienceLevelColor,
  MAX_PROFILE_QUESTIONS,
} from './interviewProfileUtils';

const THEME_GRADIENT = { background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' };

const themeButtonClass =
  'inline-flex items-center justify-center gap-1.5 text-white px-3.5 py-2 rounded-xl text-xs font-semibold shadow-[0_8px_16px_-10px_rgba(217,70,239,0.85)] hover:shadow-[0_12px_22px_-10px_rgba(14,165,233,0.5)] hover:brightness-[1.05] active:scale-[0.98] transition-all cursor-pointer whitespace-nowrap';

const EXPERIENCE_SHORT = {
  entry: 'Entry',
  junior: 'Junior',
  mid: 'Mid-level',
  senior: 'Senior',
  lead: 'Lead',
  executive: 'Executive',
};

const PROFILE_CARD_THEMES = {
  entry: {
    bar: 'from-emerald-400 to-teal-500',
    wash: 'bg-[linear-gradient(165deg,#ecfdf5_0%,#ffffff_46%)]',
    icon: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    glow: 'bg-[radial-gradient(120%_70%_at_0%_0%,rgba(16,185,129,0.18),transparent_58%)]',
    hover: 'hover:border-emerald-300 hover:shadow-[0_22px_40px_-18px_rgba(16,185,129,0.5)]',
  },
  junior: {
    bar: 'from-sky-400 to-blue-500',
    wash: 'bg-[linear-gradient(165deg,#f0f9ff_0%,#ffffff_46%)]',
    icon: 'bg-sky-50 text-sky-600 ring-sky-100',
    glow: 'bg-[radial-gradient(120%_70%_at_0%_0%,rgba(14,165,233,0.2),transparent_58%)]',
    hover: 'hover:border-sky-300 hover:shadow-[0_22px_40px_-18px_rgba(14,165,233,0.5)]',
  },
  mid: {
    bar: 'from-indigo-400 to-violet-500',
    wash: 'bg-[linear-gradient(165deg,#eef2ff_0%,#ffffff_46%)]',
    icon: 'bg-indigo-50 text-indigo-600 ring-indigo-100',
    glow: 'bg-[radial-gradient(120%_70%_at_0%_0%,rgba(99,102,241,0.18),transparent_58%)]',
    hover: 'hover:border-indigo-300 hover:shadow-[0_22px_40px_-18px_rgba(99,102,241,0.48)]',
  },
  senior: {
    bar: 'from-fuchsia-400 to-purple-500',
    wash: 'bg-[linear-gradient(165deg,#fdf4ff_0%,#ffffff_46%)]',
    icon: 'bg-fuchsia-50 text-fuchsia-600 ring-fuchsia-100',
    glow: 'bg-[radial-gradient(120%_70%_at_0%_0%,rgba(217,70,239,0.18),transparent_58%)]',
    hover: 'hover:border-fuchsia-300 hover:shadow-[0_22px_40px_-18px_rgba(217,70,239,0.48)]',
  },
  lead: {
    bar: 'from-amber-400 to-orange-500',
    wash: 'bg-[linear-gradient(165deg,#fffbeb_0%,#ffffff_46%)]',
    icon: 'bg-amber-50 text-amber-700 ring-amber-100',
    glow: 'bg-[radial-gradient(120%_70%_at_0%_0%,rgba(245,158,11,0.22),transparent_58%)]',
    hover: 'hover:border-amber-300 hover:shadow-[0_22px_40px_-18px_rgba(245,158,11,0.5)]',
  },
  executive: {
    bar: 'from-rose-400 to-pink-500',
    wash: 'bg-[linear-gradient(165deg,#fff1f2_0%,#ffffff_46%)]',
    icon: 'bg-rose-50 text-rose-600 ring-rose-100',
    glow: 'bg-[radial-gradient(120%_70%_at_0%_0%,rgba(244,63,94,0.16),transparent_58%)]',
    hover: 'hover:border-rose-300 hover:shadow-[0_22px_40px_-18px_rgba(244,63,94,0.45)]',
  },
};

const DEFAULT_CARD_THEME = {
  bar: 'from-sky-400 to-fuchsia-500',
  wash: 'bg-[linear-gradient(165deg,#f0f9ff_0%,#ffffff_52%,#fdf4ff_100%)]',
  icon: 'bg-sky-50 text-sky-600 ring-sky-100',
  glow: 'bg-[radial-gradient(120%_70%_at_0%_0%,rgba(14,165,233,0.16),transparent_55%)]',
  hover: 'hover:border-sky-300 hover:shadow-[0_22px_40px_-18px_rgba(14,165,233,0.45)]',
};

export default function InterviewProfilesTab({ selectedCompany = '', triggerCreate = 0 }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(selectedCompany || '');

  // Modals
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [savingForm, setSavingForm] = useState(false);

  // Studio
  const [studioOpen, setStudioOpen] = useState(false);
  const [activeProfileId, setActiveProfileId] = useState(null);

  // Delete Confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Notifications
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);
  const isSuperAdmin = userDetails?.is_superuser === true;

  // Fetch companies for filtering and assignment
  useEffect(() => {
    api.get('companies/')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setCompanies(list);
        if (!selectedCompany && !activeCompany) {
          if (!isSuperAdmin && userDetails.admin_companies?.length === 1) {
            const match = list.find((c) => userDetails.admin_companies.includes(c.name));
            if (match) setActiveCompany(String(match.id));
          } else if (list.length > 0 && !isSuperAdmin) {
            setActiveCompany(String(list[0].id));
          }
        }
      })
      .catch((err) => {
        console.error('Failed to load companies:', err);
        setCompanies([]);
      });
  }, [isSuperAdmin, userDetails.admin_companies, selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      setActiveCompany(selectedCompany);
    }
  }, [selectedCompany]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (experienceLevel) params.experience_level = experienceLevel;
      const compId = activeCompany || selectedCompany;
      if (compId) params.company = compId;

      const res = await api.get('interview-profiles/', { params });
      // Support paginated or direct array
      const list = res.data?.results ? res.data.results : Array.isArray(res.data) ? res.data : [];
      setProfiles(list);
    } catch (err) {
      console.error('Failed to fetch interview profiles:', err);
      setToast({ open: true, message: 'Failed to load interview profiles.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, experienceLevel, activeCompany, selectedCompany]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    if (triggerCreate > 0) {
      setEditingProfile(null);
      setFormModalOpen(true);
    }
  }, [triggerCreate]);

  // Auto-dismiss toast notification after 2 seconds
  useEffect(() => {
    if (toast.open) {
      const timer = setTimeout(() => {
        setToast((prev) => ({ ...prev, open: false }));
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [toast.open]);

  // Handle Save Profile (Create or Edit)
  const handleSaveProfile = async (formData) => {
    setSavingForm(true);
    try {
      if (editingProfile) {
        await api.patch(`interview-profiles/${editingProfile.id}/`, formData);
        setToast({ open: true, message: 'Profile details updated.', severity: 'success' });
      } else {
        const payload = { ...formData };
        const comp = formData.company || activeCompany || selectedCompany;
        if (comp) payload.company = Number(comp);
        const res = await api.post('interview-profiles/', payload);
        setToast({ open: true, message: 'New interview profile created.', severity: 'success' });
        // Automatically open studio for newly created profile
        if (res.data?.id) {
          setActiveProfileId(res.data.id);
          setStudioOpen(true);
        }
      }
      setFormModalOpen(false);
      setEditingProfile(null);
      await fetchProfiles();
    } catch (err) {
      console.error('Failed to save profile:', err);
      const errData = err.response?.data;
      let detail = 'Failed to save profile.';
      if (errData) {
        if (typeof errData === 'string') detail = errData;
        else if (errData.company) detail = Array.isArray(errData.company) ? errData.company[0] : errData.company;
        else if (errData.name) detail = Array.isArray(errData.name) ? errData.name[0] : errData.name;
        else if (errData.detail) detail = errData.detail;
        else if (errData.non_field_errors) detail = errData.non_field_errors[0];
      }
      setToast({ open: true, message: detail, severity: 'error' });
    } finally {
      setSavingForm(false);
    }
  };

  // Duplicate Profile
  const handleDuplicateProfile = async (profile) => {
    try {
      const res = await api.post(`interview-profiles/${profile.id}/duplicate/`);
      setToast({
        open: true,
        message: `Duplicated as "${res.data.name}".`,
        severity: 'success',
      });
      await fetchProfiles();
    } catch (err) {
      console.error('Failed to duplicate profile:', err);
      setToast({ open: true, message: 'Failed to duplicate profile.', severity: 'error' });
    }
  };

  // Delete Profile
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`interview-profiles/${deleteTarget.id}/`);
      setToast({ open: true, message: 'Profile deleted successfully.', severity: 'success' });
      setDeleteTarget(null);
      await fetchProfiles();
    } catch (err) {
      console.error('Failed to delete profile:', err);
      setToast({ open: true, message: 'Failed to delete profile.', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-5 lg:p-6 space-y-4 bg-slate-50/80">
      {/* Toast Notification - Floating & Auto-dismissing in 2s (does not stick or take page space) */}
      <NotificationPopup
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        severity={toast.severity}
        message={toast.message}
        duration={2000}
      />

      {/* Filter and Action Bar */}
      <div className="bg-white p-3 sm:p-3.5 rounded-2xl border border-slate-200/80 flex flex-wrap items-center justify-between gap-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[280px]">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by profile name or target role..."
              className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-2xs h-[40px]"
            />
          </div>

          {/* Experience level filter - Styled MUI Dropdown with Rounded design */}
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 160, sm: 190 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontSize: '0.8125rem',
                backgroundColor: 'white',
                height: '40px',
              },
            }}
          >
            <Select
              value={experienceLevel}
              onChange={(e) => setExperienceLevel(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                    mt: 0.5,
                  },
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All Experience Levels</MenuItem>
              {EXPERIENCE_LEVEL_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8125rem' }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Company filter - Styled MUI Dropdown with Rounded design */}
          {companies.length > 1 && (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 160, sm: 190 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontSize: '0.8125rem',
                  backgroundColor: 'white',
                  height: '40px',
                },
              }}
            >
              <Select
                value={activeCompany}
                onChange={(e) => setActiveCompany(e.target.value)}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                      mt: 0.5,
                    },
                  },
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All Companies</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8125rem' }}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </div>

      {/* Profiles List */}
      {loading ? (
        <div className="bg-white p-16 rounded-2xl border border-slate-200/80 text-center shadow-sm">
          <CircularProgress size={32} sx={{ color: '#0ea5e9' }} />
          <p className="text-xs text-slate-500 font-medium mt-2.5">Loading interview profiles...</p>
        </div>
      ) : profiles.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border border-dashed border-slate-300 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-50 to-fuchsia-50 ring-1 ring-sky-100">
            <Layers className="w-7 h-7 text-sky-500" />
          </div>
          <h3 className="text-base font-semibold text-slate-900">No Interview Profiles Found</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
            Interview Profiles are pre-packaged role-based question sets (up to 30 questions) that can be attached to Job Descriptions in one click.
          </p>
          <button
            type="button"
            onClick={() => {
              setEditingProfile(null);
              setFormModalOpen(true);
            }}
            style={THEME_GRADIENT}
            className={`mt-4 ${themeButtonClass}`}
          >
            <Plus className="w-4 h-4 stroke-[2.5]" />
            <span>Create Your First Profile</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {profiles.map((p) => {
            const theme = PROFILE_CARD_THEMES[p.experience_level] || DEFAULT_CARD_THEME;
            const expColor = getExperienceLevelColor(p.experience_level);
            const qCount = p.question_count || 0;
            const fill = Math.min(100, Math.round((qCount / MAX_PROFILE_QUESTIONS) * 100));
            const shortLevel = EXPERIENCE_SHORT[p.experience_level] || 'Unspecified';

            return (
              <article
                key={p.id}
                className={`group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200/90 ${theme.wash} shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-200 hover:-translate-y-1 ${theme.hover}`}
              >
                <div className={`h-[0.05rem] w-full bg-gradient-to-r ${theme.bar}`} />
                <div className={`pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${theme.glow}`} />

                <div className="relative z-[1] flex flex-1 flex-col p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ring-1 transition-transform duration-200 group-hover:scale-105 ${theme.icon}`}>
                      <Briefcase className="h-5 w-5" strokeWidth={2.1} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-[15px] font-semibold leading-snug text-slate-900 line-clamp-2">
                          {p.name}
                        </h3>
                        <Tooltip title={getExperienceLevelLabel(p.experience_level)}>
                          <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${expColor}`}>
                            {shortLevel}
                          </span>
                        </Tooltip>
                      </div>
                      <p className="mt-0.5 truncate text-[12px] font-medium text-slate-500">
                        {p.target_role || 'General role'}
                      </p>
                    </div>
                  </div>

                  <p className="mt-3 min-h-[40px] text-[13px] leading-relaxed text-slate-500 line-clamp-2">
                    {p.description || 'Standardized interview question profile template.'}
                  </p>

                  <div className="mt-4">
                    <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium text-slate-500">
                      <span>Questions</span>
                      <span className="font-semibold text-slate-800">
                        {qCount}
                        <span className="font-medium text-slate-400"> / {MAX_PROFILE_QUESTIONS}</span>
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full bg-gradient-to-r ${theme.bar}`}
                        style={{ width: `${fill}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
                    <Layers className="h-3.5 w-3.5 text-slate-400" />
                    <span>Used in {p.usage_count || 0} job descriptions</span>
                  </div>

                  <div className="mt-auto flex items-center justify-between gap-2 border-t border-slate-100/90 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveProfileId(p.id);
                        setStudioOpen(true);
                      }}
                      style={THEME_GRADIENT}
                      className={themeButtonClass}
                    >
                      <Layers className="h-3.5 w-3.5 stroke-[2.2]" />
                      <span>Manage Questions</span>
                    </button>

                    <div className="flex shrink-0 items-center gap-1.5">
                      <Tooltip title="Edit Profile Details">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingProfile(p);
                            setFormModalOpen(true);
                          }}
                          className="rounded-xl border border-slate-200 bg-white p-2 text-slate-400 transition-colors hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600 cursor-pointer"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </Tooltip>

                      <Tooltip title="Delete Profile">
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(p)}
                          className="rounded-xl border border-slate-200 bg-white p-2 text-slate-400 transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 cursor-pointer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Profile Form Modal (Create / Edit) */}
      <ProfileFormModal
        open={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingProfile(null);
        }}
        onSave={handleSaveProfile}
        profile={editingProfile}
        saving={savingForm}
        companies={companies}
        defaultCompany={activeCompany}
      />

      {/* Profile Question Studio */}
      <ProfileQuestionStudio
        open={studioOpen}
        onClose={() => {
          setStudioOpen(false);
          setActiveProfileId(null);
        }}
        profileId={activeProfileId}
        onProfileUpdated={fetchProfiles}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        title="Delete Interview Profile"
        message={`Are you sure you want to delete profile "${deleteTarget?.name}"? Previously attached Job Descriptions will not be affected.`}
        confirmText="Delete Profile"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        loading={deleting}
      />
    </div>
  );
}

